// CalculatorChart.js

import React from 'react';
import {
  ChartsTooltip,
  LineChart,
  ChartsLegend
} from '@mui/x-charts';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import moment from 'moment';

const colorPalettes = {
  default: ['#1f77b4', '#ff7f0e', '#2ca02c', '#d62728'],
  pastel: ['#fbb4ae', '#b3cde3', '#ccebc5', '#decbe4'],
  bright: ['#e41a1c', '#377eb8', '#4daf4a', '#984ea3'],
  muted: ['#66c2a5', '#fc8d62', '#8da0cb', '#e78ac3'],
  // Add more palettes as needed
};

const numberFormatter = (value) => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

const customTooltip = (props) => {
  const { active, payload, label } = props;
  if (active && payload && payload.length) {
    return (
      <div style={{ backgroundColor: 'white', padding: '10px', border: '1px solid #ccc', borderRadius: '5px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <p style={{ textAlign: 'right' }}>{`${label}`}</p>
        {payload.map((pld) => (
          <p key={pld.name} style={{ textAlign: 'right', color: pld.color }}>
            {`${pld.name}: ${numberFormatter(pld.value)}`}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const CalculatorChart = ({ data, calculationType, currency, colorPalette = 'muted' }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Calculate x-axis scale
  const getTicks = (data) => {
    const totalMonths = data.length;
    let interval;

    if (totalMonths <= 12) {
      interval = 1; // Show every month
    } else if (totalMonths <= 60) {
      interval = 3; // Show every quarter
    } else if (totalMonths <= 120) {
      interval = 6; // Show every half-year
    } else {
      interval = 12; // Show every year
    }

    return data.filter((_, index) => index % interval === 0).map(item => item.monthDate);
  };

  const xAxisTicks = getTicks(data);  

  const calculateCumulativeTotals = (data) => {
    let cumulativeInterest = 0;
    let cumulativeAdditionalSpending = 0;
    let cumulativePayments = 0;
    let cumulativeGrowth = 0;
    let cumulativeFees = 0;
    let cumulativeContributions = 0;
    let cumulativePrincipalPayment = 0;

    return data.map(item => {
      if (calculationType === 'mortgage') {
        cumulativeInterest += parseFloat(item.interestPayment || 0);
        cumulativePrincipalPayment += parseFloat(item.principalPayment || 0);
        cumulativePayments += parseFloat(item.payment || 0);
      } else if (calculationType === 'debt') {
        cumulativeInterest += parseFloat(item.interestAmount);
        cumulativeAdditionalSpending += parseFloat(item.additionalSpending || 0);
        cumulativePayments += parseFloat(item.contribution);
      } else {
        cumulativeGrowth += parseFloat(item.interestAmount);
        cumulativeFees += parseFloat(item.feeAmount || 0);
        cumulativeContributions += parseFloat(item.contribution);
      }

      return {
        ...item,
        cumulativeInterest,
        cumulativeAdditionalSpending,
        cumulativePayments,
        cumulativeGrowth,
        cumulativeFees,
        cumulativeContributions,
        cumulativePrincipalPayment
      };
    });
  };

  const dataWithCumulatives = calculateCumulativeTotals(data);

  const allValues = dataWithCumulatives.flatMap(item => {
    const baseValues = [
      parseFloat(item.startBalance),
      parseFloat(item.endBalance)
    ];

    switch (calculationType) {
      case 'mortgage':
        return [
          ...baseValues,
          item.cumulativeInterest,
          item.cumulativePrincipalPayment,
          item.cumulativePayments
        ];
      case 'debt':
        return [
          ...baseValues,
          item.cumulativeInterest,
          item.cumulativeAdditionalSpending,
          item.cumulativePayments
        ];
      case 'investment':
        return [
          ...baseValues,
          item.cumulativeGrowth,
          item.cumulativeFees,
          item.cumulativeContributions
        ];
      default:
        return baseValues;
    }
  });

  const minValue = Math.min(...allValues);
  const maxValue = Math.max(...allValues);

  // Add some padding to the max value to ensure all points are visible
  const yAxisMax = maxValue * 1.1;

  const xAxis = data.map(item => moment(item.monthDate).format('MMM YYYY'));

  const getDataset = () => {
    const colors = colorPalettes[colorPalette] || colorPalettes.default;

    switch (calculationType) {
      case 'mortgage':
        return [
          {
            label: 'Balance',
            data: dataWithCumulatives.map(item => parseFloat(item.endBalance).toFixed(2)),
            color: colors[0],
          },
          {
            label: 'Cumulative Interest',
            data: dataWithCumulatives.map(item => item.cumulativeInterest.toFixed(2)),
            color: colors[1],
          },
          {
            label: 'Cumulative Principal',
            data: dataWithCumulatives.map(item => item.cumulativePrincipalPayment.toFixed(2)),
            color: colors[2],
          },
          {
            label: 'Cumulative Payments',
            data: dataWithCumulatives.map(item => item.cumulativePayments.toFixed(2)),
            color: colors[3],
          },
        ];
      case 'debt':
        return [
          {
            label: 'Balance',
            data: dataWithCumulatives.map(item => parseFloat(item.endBalance).toFixed(2)),
            color: colors[0],
          },
          {
            label: 'Cumulative Interest',
            data: dataWithCumulatives.map(item => item.cumulativeInterest.toFixed(2)),
            color: colors[1],
          },
          {
            label: 'Cumulative Additional Spending',
            data: dataWithCumulatives.map(item => item.cumulativeAdditionalSpending.toFixed(2)),
            color: colors[2],
          },
          {
            label: 'Cumulative Payments',
            data: dataWithCumulatives.map(item => item.cumulativePayments.toFixed(2)),
            color: colors[3],
          },
        ];
      case 'investment':
        return [
          {
            label: 'Balance',
            data: dataWithCumulatives.map(item => parseFloat(item.endBalance).toFixed(2)),
            color: colors[0],
          },
          {
            label: 'Cumulative Growth',
            data: dataWithCumulatives.map(item => item.cumulativeGrowth.toFixed(2)),
            color: colors[1],
          },
          {
            label: 'Cumulative Fees',
            data: dataWithCumulatives.map(item => item.cumulativeFees.toFixed(2)),
            color: colors[2],
          },
          {
            label: 'Cumulative Contributions',
            data: dataWithCumulatives.map(item => item.cumulativeContributions.toFixed(2)),
            color: colors[3],
          },
        ];
      default:
        return [];
    }
  };

  const dataset = getDataset();

  return (
    <div style={{ width: '100%', height: 'auto', minHeight: '300px', maxHeight: '500px', marginBottom: 20 }}> {/* Make the chart horizontally scrollable */}
      <div style={{ width: 'auto', height: 'auto' }}> {/* Ensure the width is wide enough for scrolling */}
        <LineChart
          xAxis={[{ 
            data: xAxis,
            scaleType: 'point',
            tickLabelStyle: { 
              fontSize: isMobile ? 10 : 12, 
              angle: 45, 
              textAnchor: 'start' 
            },
            labelStyle: { 
              fontSize: isMobile ? 12 : 14,
              fontWeight: 'bold',
            },
            label: 'Date',
            tickSize: 0,
            tickMargin: isMobile ? 15 : 10,
            tickValues: xAxisTicks.map(date => date.format('MMM YYYY')),
            valueFormatter: (value) => moment(value, 'MMM YYYY').format('MMM YYYY'),
            position: 'bottom',
          }]}
          yAxis={[{ 
            label: `Amount (${currency})`,
            labelStyle: { 
              fontSize: isMobile ? 12 : 14,
              fontWeight: 'bold',
            },
            tickMargin: isMobile ? 10 : 15,
            tickLabelStyle: { fontSize: isMobile ? 10 : 12 },
            min: minValue,
            max: yAxisMax,
            tickSize: 0,
            position: 'left',
          }]}
          series={dataset.map(series => ({
            ...series,
            valueFormatter: (value) => numberFormatter(value),
            showMark: false,
          }))}
          height={isMobile ? 400 : 500}
          axisHighlight={{
            x: 'line',
            y: 'line'
          }}
          margin={{ 
            left: isMobile ? 80 : 100, 
            right: 30, 
            top: isMobile ? 120 : 60, 
            bottom: isMobile ? 120 : 100 
          }}
          sx={{
            '.MuiChartsLegend-root': {
              flexDirection: 'row',
              flexWrap: 'wrap',
              justifyContent: 'center',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
            },
            '.MuiChartsLegend-label': {
              fontSize: isMobile ? '0.7rem' : '0.875rem',
            },
            '.MuiLineElement-root': {
              strokeWidth: 2,
            },
            '.MuiMarkElement-root': {
              stroke: 'white',
              scale: '0.6',
              strokeWidth: 2,
            },
            '& .MuiChartsAxis-bottom .MuiChartsAxis-label': {
              transform: 'translateY(35px)',
            },
            '& .MuiChartsAxis-left .MuiChartsAxis-label': {
              transform: 'translateX(-50px)',
            },
          }}
          tooltip={{
            trigger: 'axis',
            formatter: (params) => {
              return params.map(item => ({
                ...item,
                value: numberFormatter(parseFloat(item.value)),
              }));
            }
          }}
        >
          <ChartsTooltip content={customTooltip} />
          <ChartsLegend 
          position={{ vertical: 'top', horizontal: 'middle' }}
          style={{ fontSize: isMobile ? '0.7rem' : '0.875rem' }}
          />
        </LineChart>
      </div>
    </div>
  );
};

export default CalculatorChart;